import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import styles from "../../../styles/components/Sections.module.css";
import AccountActionModal from "../modals/AccountActionModal";
import StatusFeedbackModal from "../modals/StatusFeedbackModal";
import ModalOverlay from "../../../ui/Modals/ModalOverlay";
import { usersService } from "../../../services/users.service";
import { oauthService } from "../../../services/oauth.service";
import { startOAuthPopup } from "../../../utils/oauthClient";
import BaseInput from "../../../ui/inputs/BaseInput/BaseInput";
import Button from "../../../ui/buttons/Button";
import { ReactComponent as EditIcon } from "../../../assets/icons/edit.svg";
import { cancelSubscription } from "../../../services/billing.service";

import googleLogo from "../../../assets/icons/google.svg";
import microsoftLogo from "../../../assets/icons/microsoft.svg";

const getInitials = (nameString) => {
  if (!nameString || typeof nameString !== "string") return "??";

  const parts = nameString.trim().split(" ");
  const initials = parts
    .slice(0, 2)
    .map((p) => (p[0] || "").toUpperCase())
    .join("");

  return initials || "??";
};

const ProfileSection = () => {
  const {
    auth,
    refreshMe,
    updateProfileName,
    updateProfileEmail,
    updateProfilePhoto,
    activeAccountId,
    supervisedAccounts = [],
  } = useAuth();

  const [mailModalOpen, setMailModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [photoModalOpen, setPhotoModalOpen] = useState(false);

  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackStatus, setFeedbackStatus] = useState("loading");
  const [feedbackTitle, setFeedbackTitle] = useState("");
  const [feedbackMessage, setFeedbackMessage] = useState("");

  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [busy, setBusy] = useState(null);
  const [showCancellationConfirm, setShowCancellationConfirm] = useState(false);
  const [cancellationLoading, setCancellationLoading] = useState(false);
  const [cancellationResult, setCancellationResult] = useState(null);
  const [cancellationError, setCancellationError] = useState("");

  const popupRef = useRef(null);
  const popupWatchRef = useRef(null);
  const fileInputRef = useRef(null);

  const userName = auth?.user?.name || "";
  const userFirstName = auth?.user?.firstName || "Sin nombre";
  const userLastName = auth?.user?.lastName || "Sin apellido";
  const avatarUrl = auth?.user?.avatar || null;
  const userEmail = auth?.user?.email || null;
  const activeAccount = supervisedAccounts.find((account) => Number(account.id) === Number(activeAccountId));
  const billingRootId = activeAccount
    ? Number(activeAccount.father_account?.id ?? activeAccount.father_account ?? activeAccount.id)
    : null;
  const canCancelSubscription = supervisedAccounts.some((account) => Number(account.id) === billingRootId);

  const hasGoogle = !!auth?.user?.has_google_oauth;
  const hasMicrosoft = !!auth?.user?.has_microsoft_oauth;

  const currentEmail = useMemo(() => {
    return userEmail || "usuario@maqmailing.io";
  }, [userEmail]);

  const mailFields = useMemo(
    () => [
      {
        name: "currentEmail",
        label: "Mail Actual",
        value: currentEmail,
        readOnly: true,
        type: "email",
      },
      {
        name: "newEmail",
        label: "Nuevo Mail",
        value: "",
        placeholder: "Nuevo Mail",
        type: "email",
        required: true,
      },
    ],
    [currentEmail],
  );

  const passwordFields = useMemo(
    () => [
      {
        name: "currentPassword",
        label: "Contraseña Actual",
        value: "",
        placeholder: "***************",
        type: "password",
        required: true,
      },
      {
        name: "newPassword",
        label: "Nueva Contraseña",
        value: "",
        placeholder: "Nueva Contraseña",
        type: "password",
        required: true,
      },
    ],
    [],
  );

  const nameFields = useMemo(
    () => [
      {
        name: "name",
        label: "Nombre",
        value: userFirstName,
        placeholder: "Jhon",
        type: "text",
        required: true,
      },
      {
        name: "lastName",
        label: "Apellido",
        value: userLastName,
        placeholder: "Doe",
        type: "text",
        required: true,
      },
    ],
    [userFirstName, userLastName],
  );

  const clearPopupWatch = () => {
    if (popupWatchRef.current) {
      window.clearInterval(popupWatchRef.current);
      popupWatchRef.current = null;
    }
  };

  const closePopup = () => {
    try {
      popupRef.current?.close();
    } catch { }
    popupRef.current = null;
  };

  useEffect(() => {
    if (!selectedPhoto) {
      setPreviewUrl("");
      return undefined;
    }

    const objectUrl = URL.createObjectURL(selectedPhoto);
    setPreviewUrl(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [selectedPhoto]);

  useEffect(() => {
    const onMessage = async (event) => {
      if (event.origin !== window.location.origin) return;

      const data = event.data;
      if (!data || typeof data !== "object") return;

      if (data.type === "oauth_error") {
        clearPopupWatch();
        closePopup();
        setBusy(null);
        return;
      }

      if (data.type === "oauth_linked" || data.type === "oauth_relinked") {
        clearPopupWatch();
        closePopup();
        setBusy(null);

        try {
          await refreshMe();
        } catch { }
      }
    };

    window.addEventListener("message", onMessage);

    return () => {
      window.removeEventListener("message", onMessage);
      clearPopupWatch();
      closePopup();
    };
  }, [refreshMe]);

  const openFeedbackLoading = () => {
    setFeedbackOpen(true);
    setFeedbackStatus("loading");
    setFeedbackTitle("Cargando...");
    setFeedbackMessage("Por favor espera mientras realizamos el cambio.");
  };

  const handleLinkOrRelink = async (provider) => {
    setBusy(provider);

    try {
      const flow =
        (provider === "google" ? hasGoogle : hasMicrosoft)
          ? "relink"
          : "link";

      popupRef.current = await startOAuthPopup({
        provider,
        flow,
        title: `${flow === "relink" ? "Re-link" : "Link"} ${provider}`,
      });

      const popup = popupRef.current;
      const startedAt = Date.now();

      clearPopupWatch();

      popupWatchRef.current = window.setInterval(() => {
        try {
          if (!popup || popup.closed) {
            clearPopupWatch();
            popupRef.current = null;
            setBusy((currentBusy) =>
              currentBusy === provider ? null : currentBusy,
            );
            return;
          }

          if (Date.now() - startedAt > 90000) {
            clearPopupWatch();
            popup.close();
            popupRef.current = null;
            setBusy((currentBusy) =>
              currentBusy === provider ? null : currentBusy,
            );
          }
        } catch {
          clearPopupWatch();
          closePopup();
          setBusy(null);
        }
      }, 400);
    } catch {
      clearPopupWatch();
      closePopup();
      setBusy(null);
    }
  };

  const handleUnlink = async (provider) => {
    setBusy(provider);
    try {
      await oauthService.unlink({ provider });
      await refreshMe();
    } catch {
    } finally {
      setBusy(null);
    }
  };

  const handleSubmitChangeEmail = async (values) => {
    setMailModalOpen(false);
    openFeedbackLoading();

    try {
      const data = await updateProfileEmail({
        email: values.newEmail,
      });

      setFeedbackStatus("success");
      setFeedbackTitle("Cambio Exitoso");
      setFeedbackMessage(
        data?.email
          ? `Tu mail fue actualizado a ${data.email}`
          : "Mail cambiado con éxito",
      );
    } catch (error) {
      const response = error?.response?.data;
      const message =
        response?.email || response?.detail || "Volvé a intentarlo en un rato";

      setFeedbackStatus("error");
      setFeedbackTitle("Algo salió mal");
      setFeedbackMessage(Array.isArray(message) ? message.join(", ") : message);
    }
  };

  const handleSubmitChangePassword = async (values) => {
    openFeedbackLoading();

    try {
      const data = await usersService.changePassword({
        oldPassword: values.currentPassword,
        newPassword: values.newPassword,
      });

      setPasswordModalOpen(false);
      setFeedbackStatus("success");
      setFeedbackTitle("Cambio Exitoso");
      setFeedbackMessage(data?.detail || "Contraseña cambiada con éxito");
    } catch (error) {
      const response = error?.response?.data;
      const message =
        response?.old_password ||
        response?.detail ||
        "Volvé a intentarlo en un rato";

      setFeedbackStatus("error");
      setFeedbackTitle("Algo salió mal");
      setFeedbackMessage(Array.isArray(message) ? message.join(", ") : message);
    }
  };

  const handleSubmitChangeName = async (values) => {
    openFeedbackLoading();

    try {
      const data = await updateProfileName({
        firstName: values.name,
        lastName: values.lastName,
      });

      setNameModalOpen(false);
      setFeedbackStatus("success");
      setFeedbackTitle("Cambio Exitoso");
      setFeedbackMessage(data?.detail || "Nombre cambiado con éxito");
    } catch (error) {
      const response = error?.response?.data;
      const message =
        response?.first_name ||
        response?.last_name ||
        response?.detail ||
        "Volvé a intentarlo en un rato";

      setFeedbackStatus("error");
      setFeedbackTitle("Algo salió mal");
      setFeedbackMessage(Array.isArray(message) ? message.join(", ") : message);
    }
  };

  const handleOpenPhotoModal = () => {
    setSelectedPhoto(null);
    setPreviewUrl("");
    setPhotoModalOpen(true);
  };

  const handleClosePhotoModal = () => {
    setPhotoModalOpen(false);
    setSelectedPhoto(null);
    setPreviewUrl("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handlePickPhoto = () => {
    fileInputRef.current?.click();
  };

  const handlePhotoChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setFeedbackOpen(true);
      setFeedbackStatus("error");
      setFeedbackTitle("Archivo inválido");
      setFeedbackMessage("Seleccioná una imagen válida.");
      return;
    }

    setSelectedPhoto(file);
  };

  const handleSubmitPhoto = async () => {
    if (!selectedPhoto) {
      setFeedbackOpen(true);
      setFeedbackStatus("error");
      setFeedbackTitle("Falta una imagen");
      setFeedbackMessage("Seleccioná una foto de perfil.");
      return;
    }

    openFeedbackLoading();

    try {
      await updateProfilePhoto({
        photo: selectedPhoto,
      });

      setPhotoModalOpen(false);
      setSelectedPhoto(null);
      setPreviewUrl("");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      setFeedbackStatus("success");
      setFeedbackTitle("Cambio Exitoso");
      setFeedbackMessage("Foto de perfil actualizada con éxito");
    } catch (error) {
      const response = error?.response?.data;
      const message =
        response?.photo || response?.detail || "Volvé a intentarlo en un rato";

      setFeedbackStatus("error");
      setFeedbackTitle("Algo salió mal");
      setFeedbackMessage(Array.isArray(message) ? message.join(", ") : message);
    }
  };

  const handleCancelSubscription = async () => {
    if (!billingRootId || !canCancelSubscription || cancellationLoading) return;
    setCancellationLoading(true);
    setCancellationError("");
    try {
      const result = await cancelSubscription(billingRootId);
      setCancellationResult(result);
      setShowCancellationConfirm(false);
    } catch (error) {
      setCancellationError(error.message || "No se pudo programar la baja.");
    } finally {
      setCancellationLoading(false);
    }
  };

  return (
    <>
      <div className={styles.sectionContent}>
        <div className={styles.profileRow}>
          <div className={styles.avatarWrapper}>
            {avatarUrl ? (
              <img className={styles.avatar} src={avatarUrl} alt={userName} />
            ) : (
              <div className={styles.avatarFallback}>
                {getInitials(userName)}
              </div>
            )}

            <button
              type="button"
              className={styles.avatarEditButton}
              onClick={handleOpenPhotoModal}
              aria-label="Editar foto de perfil"
            >
              <EditIcon style={{ display: "block" }} />
            </button>
          </div>

          <BaseInput value={userName} readOnly />
          <button
            type="button"
            className={styles.linkButton}
            onClick={() => setNameModalOpen(true)}
          >
            Editar
          </button>
        </div>

        <div className={styles.block}>
          <div className={styles.blockTitle}>Configuración de cuenta</div>

          <div className={styles.fieldRow}>
            <div className={styles.fieldBlock}>
              <div className={styles.fieldLabel}>Mail</div>
              <BaseInput
                type={mailFields[0].type}
                value={mailFields[0].value}
                readOnly
                disabled
              />
            </div>
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => setMailModalOpen(true)}
            >
              Editar
            </button>
          </div>

          <div className={styles.fieldRow}>
            <div className={styles.fieldBlock}>
              <div className={styles.fieldLabel}>Contraseña</div>
              <BaseInput
                type={passwordFields[0].type}
                value={passwordFields[0].value}
                readOnly
                placeholder={passwordFields[0].placeholder || ""}
                disabled
              />
            </div>
            <button
              type="button"
              className={styles.linkButton}
              onClick={() => setPasswordModalOpen(true)}
            >
              Editar
            </button>
          </div>
        </div>

        <div className={styles.block}>
          <div className={styles.blockTitle}>Cuentas vinculadas</div>

          <div className={styles.oauthButtonsColumn}>
            <button
              type="button"
              className={styles.oauthLoginButton}
              onClick={() => handleLinkOrRelink("microsoft")}
              disabled={!!busy}
            >
              <img src={microsoftLogo} alt="Microsoft" />
              <span>
                {busy === "microsoft"
                  ? "Conectando..."
                  : hasMicrosoft
                    ? "Re-vincular con Microsoft"
                    : "Vincular con Microsoft"}
              </span>
            </button>

            {hasMicrosoft ? (
              <button
                type="button"
                className={styles.unlinkButton}
                onClick={() => handleUnlink("microsoft")}
                disabled={!!busy}
              >
                Desvincular Microsoft
              </button>
            ) : null}

            <button
              type="button"
              className={styles.oauthLoginButton}
              onClick={() => handleLinkOrRelink("google")}
              disabled={!!busy}
            >
              <img src={googleLogo} alt="Google" />
              <span>
                {busy === "google"
                  ? "Conectando..."
                  : hasGoogle
                    ? "Re-vincular con Google"
                    : "Vincular con Google"}
              </span>
            </button>

            {hasGoogle ? (
              <button
                type="button"
                className={styles.unlinkButton}
                onClick={() => handleUnlink("google")}
                disabled={!!busy}
              >
                Desvincular Google
              </button>
            ) : null}
          </div>
        </div>

        <div className={styles.block}>
          <div className={styles.blockTitle}>Suscripción</div>
          {canCancelSubscription ? (
            <>
              <button
                type="button"
                className={styles.linkButton}
                onClick={() => setShowCancellationConfirm(true)}
                disabled={Boolean(cancellationResult)}
              >
                Dar de baja
              </button>
              {cancellationError && <p role="alert">{cancellationError}</p>}
              {cancellationResult && (
                <p role="status">
                  Baja programada. Número de gestión: <strong>{cancellationResult.case_number}</strong>
                </p>
              )}
            </>
          ) : (
            <p>Solo el administrador de la cuenta puede solicitar la baja.</p>
          )}
        </div>
      </div>

      {showCancellationConfirm && (
        <div className={styles.photoModalOverlay}>
          <section className={styles.photoModal} role="dialog" aria-modal="true" aria-labelledby="cancel-subscription-title">
            <h3 id="cancel-subscription-title">¿Dar de baja la suscripción?</h3>
            <p>La cancelación se aplicará al finalizar el período vigente. Recibirás un email con el número de gestión.</p>
            {cancellationError && <p role="alert">{cancellationError}</p>}
            <div className={styles.photoModalActions}>
              <Button variant="secondary" tone="blue" onClick={() => setShowCancellationConfirm(false)} disabled={cancellationLoading}>
                Volver
              </Button>
              <Button variant="primary" tone="blue" onClick={handleCancelSubscription} loading={cancellationLoading}>
                Confirmar baja
              </Button>
            </div>
          </section>
        </div>
      )}

      <AccountActionModal
        isOpen={nameModalOpen}
        title="Cambio de Nombre"
        fields={nameFields}
        submitText="Cambiar Nombre"
        onCancel={() => setNameModalOpen(false)}
        onSubmit={handleSubmitChangeName}
      />

      <AccountActionModal
        isOpen={mailModalOpen}
        title="Cambio de Mail"
        fields={mailFields}
        submitText="Cambiar Mail"
        onCancel={() => setMailModalOpen(false)}
        onSubmit={handleSubmitChangeEmail}
      />

      <AccountActionModal
        isOpen={passwordModalOpen}
        title="Cambio de Contraseña"
        fields={passwordFields}
        submitText="Cambiar Contraseña"
        onCancel={() => setPasswordModalOpen(false)}
        onSubmit={handleSubmitChangePassword}
      />

      {photoModalOpen ? (
        <ModalOverlay
          className={styles.photoModalOverlay}
          onClose={handleClosePhotoModal}
        >
          <div
            className={styles.photoModal}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.photoModalTitle}>
              Cambiar foto de perfil
            </div>

            <div className={styles.photoPreviewWrapper}>
              {previewUrl || avatarUrl ? (
                <img
                  className={styles.photoPreview}
                  src={previewUrl || avatarUrl}
                  alt="Preview de foto de perfil"
                />
              ) : (
                <div className={styles.photoPreviewFallback}>
                  {getInitials(userName)}
                </div>
              )}
            </div>

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoChange}
              style={{ display: "none" }}
            />

            <div className={styles.photoFileName}>
              {selectedPhoto
                ? selectedPhoto.name
                : "Ningún archivo seleccionado"}
            </div>

            <div className={styles.photoModalActions}>
              <Button
                variant="secondary"
                tone="blue"
                onClick={handleClosePhotoModal}
              >
                Cancelar
              </Button>
              <Button
                variant="secondary"
                tone="blue"
                onClick={handlePickPhoto}
              >
                Seleccionar foto
              </Button>
              <Button
                variant="primary"
                tone="blue"
                onClick={handleSubmitPhoto}
              >
                Guardar foto
              </Button>
            </div>
          </div>
        </ModalOverlay>
      ) : null}

      <StatusFeedbackModal
        isOpen={feedbackOpen}
        status={feedbackStatus}
        title={feedbackTitle}
        message={feedbackMessage}
        onClose={() => setFeedbackOpen(false)}
      />
    </>
  );
};

export default ProfileSection;
